// Certificado digital A1 (e-CNPJ) da empresa emissora: envio, termo de autorização (LGPD),
// histórico de aceites e remoção. Pode ser usado em Fiscal → Configurações ou na tela
// "Minha empresa" do sistema (import { CertificadoDigital } from "./fiscal").
import React, { useEffect, useState } from "react";
import { fiscal } from "../api";
import { dataBR, mascaraDocumento } from "../utils";
import { Aviso, Botao, Campo, Cartao, Entrada, Tabela } from "./ui";

export default function CertificadoDigital({ emissorSalvo, onAlterado, titulo = "2. Certificado digital da empresa (e-CNPJ A1)" }) {
  // Fora da tela fiscal, descobre sozinho se o emissor (CNPJ) já foi cadastrado
  const [emissor, setEmissor] = useState(undefined);
  useEffect(() => {
    if (emissorSalvo === undefined) fiscal.emissor().then(setEmissor).catch(() => setEmissor(null));
  }, [emissorSalvo]);
  const salvo = emissorSalvo !== undefined ? emissorSalvo : !!emissor;
  return <Certificado emissorSalvo={salvo} emissor={emissor} onAlterado={onAlterado} titulo={titulo} />;
}

function Certificado({ emissorSalvo = true, emissor, onAlterado, titulo }) {
  const [status, setStatus] = useState(null);
  const [termo, setTermo] = useState(null);
  const [historico, setHistorico] = useState([]);
  const [verHistorico, setVerHistorico] = useState(false);
  const [arquivo, setArquivo] = useState(null);
  const [senha, setSenha] = useState("");
  const [aceite, setAceite] = useState(false);
  const [enviando, setEnviando] = useState(false);
  const [confirmarRemocao, setConfirmarRemocao] = useState(false);
  const [msg, setMsg] = useState(null);

  const carregar = () => {
    fiscal.statusCertificado().then(setStatus).catch(() => {});
    fiscal.historicoTermos().then(setHistorico).catch(() => {});
  };
  useEffect(() => {
    carregar();
    fiscal.termoCertificado().then(setTermo).catch((e) => setMsg({ tipo: "erro", texto: e.message }));
  }, []);

  const executar = async (fn, sucesso) => {
    setEnviando(true);
    setMsg(null);
    try {
      await fn();
      if (sucesso) setMsg({ tipo: "sucesso", texto: sucesso });
      carregar();
    } catch (e) {
      setMsg({ tipo: "erro", texto: e.message });
      if (e.status === 409) fiscal.termoCertificado().then(setTermo).catch(() => {}); // termo atualizado
    } finally {
      setEnviando(false);
    }
  };

  const enviar = () => executar(async () => {
    setStatus(await fiscal.enviarCertificado(arquivo, senha, termo.versao));
    onAlterado && onAlterado();  // o backend já reenviou à integração fiscal: atualiza a situação
    setArquivo(null);
    setSenha("");
    setAceite(false);
  }, "Certificado enviado e autorização registrada.");

  const aceitarParaAtual = () => executar(async () => {
    setStatus(await fiscal.aceitarTermo(termo.versao));
    setAceite(false);
  }, "Autorização registrada para o certificado atual.");

  const remover = () => executar(async () => {
    const r = await fiscal.removerCertificado();
    onAlterado && onAlterado();
    setConfirmarRemocao(false);
    if (r.aviso) setMsg({ tipo: "atencao", texto: r.aviso });
  }, "Certificado removido e autorização revogada.");

  const cor = { ativo: "text-emerald-700", vence_em_breve: "text-amber-700", expirado: "text-red-700", ausente: "text-gray-600" }[status?.status] || "";
  const rotulo = { ativo: "Ativo", vence_em_breve: "Vence em breve", expirado: "Expirado", ausente: "Não enviado" }[status?.status] || "…";
  const termoPendente = status?.possui_certificado && termo && status.termo_versao !== termo.versao;

  const caixaTermo = termo && (
    <div className="space-y-2">
      <div className="max-h-56 overflow-y-auto whitespace-pre-line rounded border border-gray-200 bg-gray-50 p-3 text-xs leading-relaxed text-gray-700" tabIndex={0}>
        {termo.texto}
      </div>
      <label className="flex items-start gap-2 text-sm">
        <input type="checkbox" className="mt-0.5 h-4 w-4" checked={aceite} onChange={(e) => setAceite(e.target.checked)} />
        <span>Li e aceito o Termo de Autorização de Uso do Certificado Digital (versão {termo.versao}) e declaro ter poderes para autorizar em nome da empresa.</span>
      </label>
    </div>
  );

  return (
    <Cartao titulo={titulo}>
      {!emissorSalvo && (
        <div className="mb-3"><Aviso tipo="atencao">Primeiro preencha e salve os dados do emissor em Fiscal → Configurações. O certificado é conferido contra o CNPJ cadastrado lá.</Aviso></div>
      )}
      {emissor && (
        <p className="mb-3 text-sm text-gray-600">Empresa emissora das notas: <strong>{emissor.razao_social}</strong> — CNPJ {mascaraDocumento(emissor.cnpj)}.
          Envie o certificado e-CNPJ desta empresa.</p>
      )}
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <div className="space-y-1 text-sm">
          <p>Status: <strong className={cor}>{rotulo}</strong></p>
          {status?.valido_ate && <p>Válido até: <strong>{dataBR(status.valido_ate)}</strong> ({status.dias_restantes} dias)</p>}
          {status?.titular && <p className="text-gray-600">{status.titular}</p>}
          {status?.possui_certificado && (
            status.termo_versao
              ? <p className="text-gray-600">Autorização: versão {status.termo_versao}, aceita em {dataBR(status.termo_aceito_em, true)}{status.termo_aceito_por ? ` por ${status.termo_aceito_por}` : ""}</p>
              : <p className="text-amber-700">Autorização de uso ainda não aceita — a emissão fica bloqueada até o aceite.</p>
          )}
          <p className="pt-2 text-xs text-gray-500">
            Somente o e-CNPJ A1 (.pfx) da própria empresa emitente. A SEFAZ não aceita notas assinadas com certificado de outra empresa
            ou de pessoa física (e-CPF), inclusive de contador com procuração. O arquivo e a senha são criptografados e nunca ficam
            visíveis aqui; você recebe um e-mail 30 dias antes do vencimento.
          </p>
          <div className="flex flex-wrap gap-2 pt-2">
            {historico.length > 0 && <Botao variante="fantasma" onClick={() => setVerHistorico((v) => !v)}>{verHistorico ? "Ocultar" : "Ver"} registros de autorização</Botao>}
            {status?.possui_certificado && !confirmarRemocao && <Botao variante="fantasma" className="text-red-600" onClick={() => setConfirmarRemocao(true)}>Remover certificado</Botao>}
          </div>
          {confirmarRemocao && (
            <Aviso tipo="atencao">
              Remover o certificado revoga a autorização, pede a remoção na plataforma de transmissão e bloqueia novas emissões até um novo envio.
              <span className="mt-2 flex gap-2">
                <Botao variante="perigo" carregando={enviando} onClick={remover}>Remover e revogar</Botao>
                <Botao variante="secundario" onClick={() => setConfirmarRemocao(false)}>Voltar</Botao>
              </span>
            </Aviso>
          )}
        </div>

        <div className="space-y-3">
          {termoPendente ? (
            <>
              <p className="text-sm font-medium">{status.termo_versao ? "O termo foi atualizado. Leia e aceite a nova versão:" : "Aceite a autorização de uso do certificado atual:"}</p>
              {caixaTermo}
              <Botao carregando={enviando} disabled={!aceite} onClick={aceitarParaAtual}>Registrar aceite</Botao>
              <p className="text-xs text-gray-500">Ou envie um novo certificado abaixo.</p>
            </>
          ) : null}
          <Campo rotulo={status?.possui_certificado ? "Substituir certificado (.pfx)" : "Arquivo do certificado (.pfx)"}>
            <input type="file" accept=".pfx,.p12" onChange={(ev) => setArquivo(ev.target.files?.[0] || null)} className="text-sm" />
          </Campo>
          <Campo rotulo="Senha do certificado">
            <Entrada type="password" value={senha} autoComplete="new-password" onChange={(ev) => setSenha(ev.target.value)} />
          </Campo>
          {!termoPendente && caixaTermo}
          <Botao carregando={enviando} disabled={!emissorSalvo || !arquivo || !senha || !aceite || !termo} onClick={enviar}>Aceitar e enviar certificado</Botao>
        </div>
      </div>
      {msg && <div className="mt-3"><Aviso tipo={msg.tipo}>{msg.texto}</Aviso></div>}
      {verHistorico && (
        <div className="mt-4">
          <Tabela
            chave={(r) => r.id}
            linhas={historico}
            colunas={[
              { id: "ocorrido_em", rotulo: "Data e hora", render: (r) => dataBR(r.ocorrido_em, true) },
              { id: "evento", rotulo: "Evento", render: (r) => (r.evento === "aceite" ? "Aceite" : "Revogação") },
              { id: "versao", rotulo: "Versão" },
              { id: "usuario", rotulo: "Usuário", render: (r) => r.email || r.user_id },
              { id: "ip", rotulo: "IP" },
              { id: "cert", rotulo: "Certificado", render: (r) => `${r.certificado?.titular || ""} · série ${r.certificado?.serial || ""}` },
            ]}
          />
        </div>
      )}
    </Cartao>
  );
}

